import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/api/auth/forgot-password`, { email });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <div className="bg-blue-600 rounded-xl p-2 shadow-lg inline-block">
                            <div className="border-2 border-white rounded-lg px-6 py-2">
                                <span className="font-black text-2xl text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                                    PHONEB2B
                                </span>
                            </div>
                        </div>
                    </Link>
                </div>

                <Card className="shadow-xl border-0">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-2xl font-bold text-slate-900">
                            Mot de passe oublié ?
                        </CardTitle>
                        <CardDescription className="text-slate-500">
                            {sent 
                                ? "Vérifiez votre boîte email"
                                : "Entrez votre adresse email pour recevoir un lien de réinitialisation"
                            }
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-4">
                        {sent ? (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="font-semibold text-slate-900 mb-2">Email envoyé !</h3>
                                <p className="text-slate-600 text-sm mb-6">
                                    Si un compte existe avec l'adresse <strong>{email}</strong>, 
                                    vous recevrez un lien de réinitialisation dans quelques minutes.
                                </p>
                                <p className="text-slate-500 text-xs mb-6">
                                    Pensez à vérifier vos spams si vous ne trouvez pas l'email.
                                </p>
                                <Link to="/login">
                                    <Button variant="outline" className="w-full">
                                        <ArrowLeft className="w-4 h-4 mr-2" />
                                        Retour à la connexion
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Adresse email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="votre@email.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="pl-10"
                                            required
                                            data-testid="forgot-email-input"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                        {error}
                                    </div>
                                )}

                                <Button 
                                    type="submit" 
                                    className="w-full bg-blue-600 hover:bg-blue-700"
                                    disabled={loading}
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        'Envoyer le lien de réinitialisation'
                                    )}
                                </Button>

                                <div className="text-center pt-2">
                                    <Link 
                                        to="/login" 
                                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        <ArrowLeft className="w-4 h-4 inline mr-1" />
                                        Retour à la connexion
                                    </Link>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>

                <p className="text-center text-sm text-slate-500 mt-6">
                    Pas encore de compte ?{' '}
                    <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                        Demander un accès
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
